/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react'
import './EditPhoto.css'
import { Modal } from 'react-bootstrap'
import { Button } from '@components/common'
import Webcam from 'react-webcam'
import Cropper, { ReactCropperElement } from 'react-cropper'
import 'cropperjs/dist/cropper.css'
import { SliderComponent } from '@syncfusion/ej2-react-inputs'
import { useAppDispatch } from '@store/hooks'
import { updatePatientPhoto, updateProviderPhoto } from '@store/actions'
import { refreshFromPatient } from '@store/reducers/patientmanagement.reducer'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { logger } from '@utils/safeLogger'
export type EditPhotoEntityType = 'patient' | 'provider'

type EditPhotoProps = {
  show: boolean
  onHide: () => void
  entityType?: EditPhotoEntityType
  entityId?: string
  backdropClassName?: string
  customClassName?: string
}

const loadingContainer = '.edit-photo-modal'

const EditPhoto: React.FC<EditPhotoProps> = ({
  show,
  onHide,
  entityType = 'patient',
  entityId,
  backdropClassName,
  customClassName,
}) => {
  const entityIdResolved = entityId || ''
  const dispatch = useAppDispatch()
  const [source, setSource] = useState<'file' | 'camera' | null>(null)
  const [isCropping, setIsCropping] = useState(false)
  const webcamRef = React.useRef<Webcam>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileImageSrc, setFileImageSrc] = useState<string | null>(null)
  const cropperRef = useRef<ReactCropperElement>(null)
  const [zoom, setZoom] = useState(1)
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  async function fileFromBase64(
    input: string,
    filename: string,
    mimeType?: string,
  ): Promise<File> {
    const isDataUrl = input.startsWith('data:')
    const dataUrl = isDataUrl ? input : `data:${mimeType || ''};base64,${input}`

    const res = await fetch(dataUrl)
    if (!res.ok)
      throw new Error(
        `Failed to parse base64/data URL: ${res.status} ${res.statusText}`,
      )

    const blob = await res.blob()

    const detected = isDataUrl ? dataUrl.match(/^data:([^;]+)/)?.[1] || '' : ''
    const type =
      mimeType?.trim() || detected || blob.type || 'application/octet-stream'

    return new File([blob], filename, { type })
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const elFirstButton = document.querySelector(
        '.upload-photo-container .e-first-button',
      )
      const elSecondButton = document.querySelector(
        '.upload-photo-container .e-second-button',
      )
      if (elFirstButton && elSecondButton) {
        elFirstButton.textContent = '-'
        elSecondButton.textContent = '+'
        clearInterval(interval)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [])

  const onSuccess = (): void => {
    hideLoading(loadingContainer)
    onHide()
    showToast(
      createSuccessToast(
        'Photo updated successfully',
        'Photo has been updated successfully.',
      ),
    )
  }

  const onError = (): void => {
    hideLoading(loadingContainer)
    onHide()
    showToast(
      createFailedToast(
        'Failed to update photo',
        'Failed to update photo. Please try again.',
      ),
    )
  }

  const saveFinalImage = React.useCallback(
    async (imageSrc: string) => {
      const convertedFile = await fileFromBase64(imageSrc!, 'photo.jpg')
      if (!entityIdResolved) return

      showLoading({
        container: loadingContainer,
        hideElements: ['.calendar-container'],
      })
      try {
        if (entityType === 'provider') {
          await dispatch(
            updateProviderPhoto({
              providerId: entityIdResolved,
              file: convertedFile,
            }),
          ).unwrap()
          onSuccess()
        } else {
          await dispatch(
            updatePatientPhoto({
              patientId: entityIdResolved,
              file: convertedFile,
            }),
          ).unwrap()
          dispatch(refreshFromPatient())
          setTimeout(() => {
            onSuccess()
          }, 2000)
        }
      } catch (error) {
        logger.error('Error saving photo:', error)
        if (entityType === 'provider') {
          onError()
        } else {
          setTimeout(() => {
            onError()
          }, 2000)
        }
      }
    },
    [
      entityIdResolved,
      entityType,
      dispatch,
      onHide,
      showLoading,
      hideLoading,
      showToast,
    ],
  )

  const crop = React.useCallback(async () => {
    const croppedImage = cropperRef.current?.cropper
      .getCroppedCanvas()
      .toDataURL()
    saveFinalImage(croppedImage!)
  }, [cropperRef])

  const capture = React.useCallback(async () => {
    const imageSrc = webcamRef.current?.getScreenshot()
    setFileImageSrc(imageSrc!)
    setSource('file')
  }, [webcamRef])

  const fileUpload = (files: FileList | null): void => {
    if (!files) return
    setSource('file')
    setIsCropping(true)
    const url = URL.createObjectURL(files[0])
    setFileImageSrc(url)
  }

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    fileUpload(event.target.files)
  }

  const handleTakePhoto = (): void => {
    setSource('camera')
    setIsCropping(true)
  }

  const handleConfirm = (): void => {
    crop()
  }

  const handleZoomChange = React.useCallback(
    (args: any): void => {
      setZoom(args.value)
      const cropper: any = cropperRef.current?.cropper
      if (cropper) {
        cropper.scale(args.value)
      }
    },
    [cropperRef],
  )

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
    fileUpload(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <Modal
      show={show}
      onHide={() => onHide()}
      centered
      className={['edit-photo-modal', customClassName]
        .filter(Boolean)
        .join(' ')}
      backdropClassName={backdropClassName}
      backdrop="static"
    >
      <Modal.Header className="bg-custom3" closeButton>
        <Modal.Title>Edit photo</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div
          className="upload-photo-container"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {source === 'file' && (
            <>
              <Cropper
                src={fileImageSrc!}
                style={{ height: 350, width: '100%' }}
                aspectRatio={1}
                guides={true}
                ref={cropperRef}
                dragMode="none"
                movable={false}
                zoomOnWheel={false}
                zoomOnTouch={false}
                scalable={true}
                ready={() => {
                  const c: any = cropperRef.current?.cropper
                  if (!c) return
                  const img = c.getImageData()
                  c.setData({
                    x: 0,
                    y: 0,
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                  })
                }}
              />
              <div className="slider-container" style={{ width: '60%' }}>
                <SliderComponent
                  value={zoom}
                  max={2}
                  min={1}
                  step={0.01}
                  change={handleZoomChange}
                  showButtons={true}
                />
                <span className="zoom-value">{(zoom * 100).toFixed(0)}%</span>
              </div>
            </>
          )}
          {source === 'camera' && (
            <>
              <Webcam
                audio={false}
                height={348}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                width={348}
                videoConstraints={{
                  width: 720,
                  height: 720,
                  facingMode: 'user',
                }}
              />
            </>
          )}
          {source === null && (
            <>
              <i className="mgc_file_upload_fill"></i>
              <p className="mb-0">
                Drag and drop file here or{' '}
                <button
                  type="button"
                  className="unstyled-button link-choose-file"
                  onClick={() => fileInputRef.current?.click()}
                >
                  choose file
                </button>
              </p>
              <p className="text-muted mb-0">Max upload file: 5 MB</p>
              <div className="d-flex gap-2 action-buttons">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden-file-input"
                  onChange={handleFileUpload}
                  accept=".png,.jpg,.jpeg"
                />
                <Button
                  size="md"
                  id="upload-file-button"
                  className="button button-primary"
                  leftIcon={<i className="mgc_upload_2_line"></i>}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload file
                </Button>
                <Button
                  size="md"
                  id="take-photo-button"
                  className="button button-secondary"
                  leftIcon={<i className="mgc_camera_2_line"></i>}
                  onClick={handleTakePhoto}
                >
                  Take photo
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal.Body>

      {isCropping && (
        <Modal.Footer>
          <Button
            size="md"
            className="button-secondary"
            onClick={() => onHide()}
          >
            Cancel
          </Button>
          {source === 'camera' ? (
            <Button size="md" className="button-primary" onClick={capture}>
              Capture
            </Button>
          ) : (
            <Button
              size="md"
              className="button-primary"
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          )}
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default EditPhoto
